import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import { Lock, EyeOff, Hand, MapPin, ShieldCheck, ArrowRight } from "lucide-react";

const COMMITMENTS = [
  { icon: Lock,        h: "Privacy-first design",
    body: "We collect the minimum needed to serve you. Onboarding works without an email or name." },
  { icon: EyeOff,      h: "Optional anonymity",
    body: "A device identifier is enough to use Sister Signal. Email is only required if you want recovery across devices." },
  { icon: Hand,        h: "Quick Exit",
    body: "One tap disguises the app as a calculator. The disguise is convincing on lock screens and during a quick glance." },
  { icon: MapPin,      h: "User-controlled data",
    body: "Export, delete, or pause any data we hold at any time. No dark patterns, no hidden retention." },
  { icon: ShieldCheck, h: "No selling, no ads",
    body: "We do not and will not sell user data. Sister Signal is not ad-supported and has no third-party trackers." },
];

const TECHNICAL = [
  ["Local-first sensitive data", "Incident logs, journal entries, and decode history are scoped to your account and never shared with other users."],
  ["Encryption in transit and at rest", "Connections are HTTPS; database storage uses provider-level encryption."],
  ["Trusted Circle SMS", "Sent via Twilio's Messaging Service with carrier-level STOP handling. The first message each contact receives includes a clear opt-out line."],
  ["No location tracking", "Approximate location can be optionally attached to a check-in alert. It is never tracked passively."],
];

export default function SafetyPrivacy() {
  return (
    <MarketingShell>
      <Seo title="Safety & Privacy" description="Privacy-first design, optional anonymity, Quick Exit, user-controlled data — Sister Signal's safety commitments." path="/safety-privacy" />
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid="page-safety-privacy">
        <p className="font-script text-2xl text-brand">how we treat your story</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Safety & privacy.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
          Sister Signal is a safety tool first. Every design decision passes through one question:
          <em> would this be safe if someone took the phone right now?</em>
        </p>

        <section className="mt-12">
          <h2 className="font-serif text-3xl">Five commitments.</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {COMMITMENTS.map((c) => (
              <div key={c.h} className="rounded-2xl bg-card border border-border p-6">
                <div className="w-9 h-9 grid place-items-center rounded-full bg-brand/10 text-brand">
                  <c.icon className="w-4 h-4" />
                </div>
                <p className="mt-3 font-serif text-xl">{c.h}</p>
                <p className="mt-1 text-foreground/75 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-serif text-3xl">Technical details, plainly.</h2>
          <ul className="mt-5 space-y-3">
            {TECHNICAL.map(([h, body]) => (
              <li key={h} className="rounded-2xl bg-card border border-border p-5">
                <p className="font-serif text-lg">{h}</p>
                <p className="mt-1 text-foreground/75 leading-relaxed">{body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl bg-brand-soft/40 border border-brand/20 p-8">
          <p className="font-script text-xl text-brand">if you are in immediate danger</p>
          <p className="mt-2 text-lg text-foreground/85 leading-relaxed">
            Sister Signal is an emotional safety companion, not an emergency service. If you are in
            immediate danger, please call <strong>911</strong>. The National Domestic Violence Hotline is
            available 24/7 at <strong>1-800-799-7233</strong> (text START to 88788).
          </p>
        </section>

        <p className="mt-10 text-sm text-foreground/55">
          For the full legal text, see our <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link> and
          {" "}<Link to="/terms" className="text-brand hover:underline">Terms of Service</Link>.
        </p>

        <Link to="/app" data-testid="safety-open-app"
          className="mt-8 inline-flex items-center gap-1 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
          Open the app <ArrowRight className="w-4 h-4" />
        </Link>
      </article>
    </MarketingShell>
  );
}
